import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ativosDaColetiva, ehColetiva, findCuisine, findMeal, linhaDaGalera, pointsLabel, prazoDaMissao, progressoDaMissao,
  recadoDoProgresso, refeicoesDaMissao, resolveConfig, semRepeticoesVelhas, situacaoDaMissao, tipoDaMissao,
  type ChallengeConfig, type ChallengeEvent,
} from "@nhomi/core";
import { useSession } from "@/lib/auth";
import { encerrarMissao, tirarDaMissao, watchChallenge, type Challenge } from "@/data/challenges";
import { watchMembers, type Member } from "@/data/members";
import { dayKey } from "@/data/plates";
import { Avatar, Empty, Spinner, TopBar } from "@/ui/primitives";
import { PedidoDaMissao, Progresso } from "@/ui/Missoes";

/* ============================================================
   Missões

   Uma tela para o que antes não tinha lugar: o que o grupo está
   tentando fechar, quanto falta para mim e onde está a galera. A galera
   é o que faz missão funcionar em grupo — ver que dois já fecharam a
   "Semana leve" é o empurrão que o número sozinho não dá.
   ============================================================ */

export function Missoes() {
  const { cid = "" } = useParams();
  const { user } = useSession();
  const [challenge, setChallenge] = useState<Challenge | null | undefined>(undefined);
  const [members, setMembers] = useState<Member[]>([]);

  useEffect(() => watchChallenge(cid, setChallenge), [cid]);
  useEffect(() => watchMembers(cid, setMembers), [cid]);

  const config = useMemo(() => resolveConfig(challenge), [challenge]);
  const hoje = dayKey();

  if (challenge === undefined) return <div className="screen center"><Spinner /></div>;
  if (!challenge) {
    return <main className="screen"><Empty emoji="🤔" title="Desafio não encontrado" /></main>;
  }

  const eu = members.find((m) => m.uid === user?.uid);
  const lista = semRepeticoesVelhas(config.events);
  const grupos = {
    valendo: lista.filter((e) => situacaoDaMissao(e, hoje) === "valendo"),
    breve: lista.filter((e) => situacaoDaMissao(e, hoje) === "breve"),
    acabou: lista.filter((e) => situacaoDaMissao(e, hoje) === "acabou"),
  };
  const souDono = challenge.ownerUid === user?.uid;
  const cartao = (e: ChallengeEvent) => (
    <CartaoDaMissao key={e.id} e={e} eu={eu} members={members} hoje={hoje} cid={cid} config={config} souDono={souDono} />
  );

  return (
    <main className="screen">
      <TopBar title="Missões" back={`/c/${cid}`} />
      <div className="screen-body no-tabbar">
        {souDono && (
          <Link className="btn btn-primary" to={`/c/${cid}/missoes/nova`}>+ Nova missão</Link>
        )}
        {config.events.length === 0 ? (
          <Empty emoji="🎯" title="Nenhuma missão ainda">
            <p>
              {souDono
                ? "“Semana leve”, “Zero açúcar”, “Ninguém fura”… escolha um modelo e ajuste em dois toques."
                : "Quem criou o desafio pode criar missões com prazo e prêmio."}
            </p>
          </Empty>
        ) : (
          <>
            {grupos.valendo.length > 0 && <div className="section-label left">Valendo agora</div>}
            {grupos.valendo.map(cartao)}
            {grupos.breve.length > 0 && <div className="section-label left">Em breve</div>}
            {grupos.breve.map(cartao)}
            {grupos.acabou.length > 0 && <div className="section-label left">Encerradas</div>}
            {grupos.acabou.map(cartao)}
            {souDono && (
              <div className="hint-row center">
                <Link to={`/c/${cid}/regras`}>Editar todas as missões em Regras</Link>
              </div>
            )}
          </>
        )}
        <div className="gap" />
      </div>
    </main>
  );
}

function CartaoDaMissao(
  { e, eu, members, hoje, cid, config, souDono }: {
    e: ChallengeEvent; eu?: Member; members: Member[]; hoje: string; cid: string;
    config: ChallengeConfig; souDono: boolean;
  },
) {
  const navigate = useNavigate();
  const fora = new Set(e.fora ?? []);
  const valendo = situacaoDaMissao(e, hoje) !== "acabou";
  const meu = eu?.events?.[e.id];
  const p = progressoDaMissao(e, meu ?? { cuisineIds: [], bonus: 0, day: "" }, hoje);
  const vazio = { cuisineIds: [], bonus: 0, day: "" as const };

  /* A galera, de quem está mais perto de fechar para quem está mais
     longe. Quem fechou primeiro vem antes entre os que fecharam. */
  const galera = members
    .map((m) => ({ m, p: progressoDaMissao(e, m.events?.[e.id] ?? vazio, hoje), dia: m.events?.[e.id]?.day ?? "" }))
    .sort((a, b) =>
      Number(b.p.fechou) - Number(a.p.fechou)
      || (a.p.fechou ? a.dia.localeCompare(b.dia) : 0)
      || (ehColetiva(e)
        ? (b.m.events?.[e.id]?.meus ?? 0) - (a.m.events?.[e.id]?.meus ?? 0)
        : b.p.feitos - a.p.feitos)
      || a.m.name.localeCompare(b.m.name, "pt-BR"));
  const fecharam = galera.filter((g) => (g.m.events?.[e.id]?.bonus ?? 0) > 0).length;

  return (
    <div className={`card missao-card ${p.fechou ? "fechou" : ""}`}>
      <div className="missao-cabeca">
        <span className="missao-emoji">{e.emoji || "🎯"}</span>
        <div className="missao-titulo">
          <div className="missao-nome">{e.name}</div>
          <div className="missao-pede"><PedidoDaMissao e={e} config={config} /></div>
        </div>
        <span className="missao-premio">+{pointsLabel(e.bonus)}</span>
      </div>

      {tipoDaMissao(e) === "colecao" && e.cuisineIds.length > 0 && (
        <div className="missao-selos">
          {e.cuisineIds.map((id) => {
            const c = findCuisine(config, id);
            const tenho = meu?.cuisineIds?.includes(id);
            return (
              <span key={id} className={`chip ${tenho ? "active" : ""}`}>
                {c?.emoji} {c?.label ?? id}
              </span>
            );
          })}
        </div>
      )}

      {refeicoesDaMissao(e).length > 0 && (
        <div className="missao-selos">
          {refeicoesDaMissao(e).map((id) => {
            const m = findMeal(config, id);
            return (
              <span key={id} className="chip">
                {m?.emoji || "🍽️"} {m?.label ?? id}
              </span>
            );
          })}
        </div>
      )}

      <div className="missao-eu">
        <div className="missao-eu-topo">
          <span>{ehColetiva(e) ? "O grupo" : "Você"}</span>
          <b>{p.feitos}/{p.need}</b>
        </div>
        <Progresso e={e} estado={meu} hoje={hoje} grande />
        <div className="missao-eu-sub">
          {meu?.fora ? "O dono tirou você desta missão" : recadoDoProgresso(e, meu, hoje)} · {prazoDaMissao(e, hoje)}
          {e.repetir && " · renova toda segunda"}
        </div>
        {tipoDaMissao(e) === "coletivaTodoDia" && (() => {
          const a = ativosDaColetiva(e, members, hoje);
          return <div className="missao-ativos">{a.ativos} de {a.total} ativos contam · quem não posta há 7 dias fica de fora</div>;
        })()}
      </div>

      {members.length > 1 && (
        <div className="missao-galera">
          <div className="missao-galera-titulo">
            A galera{fecharam > 0 && ` · ${fecharam} ${fecharam === 1 ? "ganhou" : "ganharam"}`}
          </div>
          {galera.map(({ m }) => {
            const tirado = fora.has(m.uid);
            const linha = tirado ? { texto: "fora desta missão", ok: false } : linhaDaGalera(e, m.events?.[e.id], hoje);
            return (
              <div key={m.uid} className={`missao-pessoa ${tirado ? "fora" : ""}`}
                role="link" tabIndex={0} onClick={() => navigate(`/c/${cid}/u/${m.uid}`)}>
                <Avatar name={m.name} photo={m.photo} size="xs" />
                <span className="missao-pessoa-nome">{m.uid === eu?.uid ? "Você" : m.name}</span>
                <span className={linha.ok ? "missao-pessoa-ok" : "missao-pessoa-num"}>{linha.texto}</span>
                {souDono && valendo && (
                  <button className="missao-pessoa-tirar"
                    aria-label={tirado ? `Voltar ${m.name} pra missão` : `Tirar ${m.name} da missão`}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      const msg = tirado
                        ? `${m.name} volta a contar nesta missão?`
                        : `Tirar ${m.name} desta missão? Não ganha o bônus e não conta no "todo mundo". Continua no grupo.`;
                      if (window.confirm(msg)) void tirarDaMissao(cid, e.id, m.uid, !tirado);
                    }}>{tirado ? "↩" : "⋯"}</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {souDono && valendo && (
        <div className="missao-acoes">
          <Link className="btn btn-white" to={`/c/${cid}/regras`}>Editar</Link>
          <button className="btn btn-ghost" onClick={() => {
            if (window.confirm(`Encerrar “${e.name}” hoje? Quem já ganhou continua com o bônus.`)) {
              void encerrarMissao(cid, e.id, hoje);
            }
          }}>Encerrar hoje</button>
        </div>
      )}
    </div>
  );
}
